<template>
  <v-card outlined>
    <v-card-title>
      {{ $t("auditor.stepModelRequirementForm.resultForm.title") }}
    </v-card-title>

    <v-card-text>
      <AssistantResultForm :requirement="requirement" @addToResult="addToResult" model="step" />

      <v-form ref="form" v-model="valid">
        <v-checkbox
          v-model="formData.applicable"
          :label="$t('auditor.stepModelRequirementForm.resultForm.result.applicable')"
          @change="save"
        ></v-checkbox>

        <v-text-field
          v-if="formData.applicable"
          v-model="formData.points"
          :label="
            `${$t('auditor.stepModelRequirementForm.resultForm.points.label')} (${requirement.minPoints} - ${
              requirement.maxPoints
            }, bonus: ${requirement.bonus})`
          "
          :min="requirement.minPoints"
          :max="requirement.maxPoints + requirement.bonus"
          :rules="[
            (v) => v == 0 || !!v || $t('validation.required'),
            (v) => v >= requirement.minPoints || $t('validation.tooLow'),
            (v) => v <= requirement.maxPoints + requirement.bonus || $t('validation.tooHigh'),
          ]"
          type="number"
          @change="save"
          required
        ></v-text-field>

        <MarkdownEditor
          v-model="formData.description"
          :label="$t('auditor.stepModelRequirementForm.resultForm.description.label')"
          :rules="[(v) => !!v || $t('validation.required')]"
          @change="save"
        />
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-btn color="primary" @click="save" :disabled="!valid" :loading="loading">
        {{ $t("auditor.stepModelRequirementForm.resultForm.save") }}
      </v-btn>

      <v-spacer />

      <v-btn v-if="requirement.state === 'DONE'" color="secondary" @click="remove" :loading="loading">
        {{ $t("auditor.stepModelRequirementForm.resultForm.remove") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { pick } from "ramda";
import UpdateStepModelRequirementResultMutation from "@/gql/mutations/UpdateStepModelRequirementResultMutation.gql";
import RemoveStepModelRequirementResultMutation from "@/gql/mutations/RemoveStepModelRequirementResultMutation.gql";
import AssistantResultForm from "@/components/Audit/Fund/AssistantResultForm.vue";
import MarkdownEditor from "@/components/Common/MarkdownEditor.vue";

export default {
  components: {
    AssistantResultForm,
    MarkdownEditor,
  },

  props: {
    requirement: {
      type: Object,
      required: true,
    },

    fund: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      formData: null,
      valid: false,
      loading: false,
    };
  },

  computed: {
    addTextEnabled() {
      if (!this.formData.applicable) return true;
      if (!this.formData.points) return false;
      if (this.formData.points.length === 0) return false;

      return (
        this.formData.points >= this.requirement.minPoints &&
        this.formData.points <= this.requirement.maxPoints + this.requirement.bonus
      );
    },
  },

  methods: {
    async save() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      this.formData.points = parseFloat(this.formData.points);

      await this.$apollo.mutate({
        mutation: UpdateStepModelRequirementResultMutation,
        variables: {
          id: this.requirement.id,
          input: this.formData,
        },
      });

      this.loading = false;
      this.$emit("save");
    },

    async remove() {
      this.loading = true;

      await this.$apollo.mutate({
        mutation: RemoveStepModelRequirementResultMutation,
        variables: {
          id: this.requirement.id,
        },
      });

      this.loading = false;
      this.$emit("remove");
    },

    addToResult(result) {
      this.formData.description = result;
      this.save();
    },
  },

  watch: {
    // update form data if data is unchanged
    // that way the form is updated if the data has not been altered/worked on
    // if the data has been changed, it will not be updated
    requirement(requirement, oldRequirement) {
      if (oldRequirement.result.points === this.formData.points) this.formData.points = requirement.result.points;

      if (oldRequirement.result.description === this.formData.description)
        this.formData.description = requirement.result.description;

      if (oldRequirement.result.applicable === this.formData.applicable)
        this.formData.applicable = requirement.result.applicable;
    },
  },

  created() {
    this.formData = pick(["points", "description", "applicable"], this.requirement.result);
  },
};
</script>
